/** @file Edits a mirrorFix direction's fix: overlay skin, draw-order ops and per-base fix animations. */
import { z } from 'zod';
import type { Draft } from 'immer';
import { mirrorAsymmetry } from '@pose2d/format/doc/mirrorAsymmetry';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { DirectionEntry, DrawOrderOp, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { currentOf } from '../_shared/currentOf';
import { directionsDraft } from './directionsDraft';

const ref = z.string().min(1);
const opSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('swap'), a: ref, b: ref }).strict(),
  z.object({ op: z.literal('moveAbove'), slot: ref, ref }).strict(),
  z.object({ op: z.literal('moveBelow'), slot: ref, ref }).strict(),
]);

type OpInput = z.infer<typeof opSchema>;
type Fix = NonNullable<DirectionEntry['fix']>;
type FixInput = { skin?: string | null; drawOrderOps?: OpInput[]; animations?: Record<string, string | null> };

/** The op with its slot names/ids resolved to ids. */
function resolveOp(ctx: CommandContext, op: OpInput): DrawOrderOp {
  const slot = (r: string): string => ctx.resolve('slot', r);
  switch (op.op) {
    case 'swap':
      return { op: 'swap', a: slot(op.a), b: slot(op.b) };
    case 'moveAbove':
      return { op: 'moveAbove', slot: slot(op.slot), ref: slot(op.ref) };
    case 'moveBelow':
      return { op: 'moveBelow', slot: slot(op.slot), ref: slot(op.ref) };
  }
}

/** The fix skin after `skin`: kept when omitted, removed by null, else resolved. */
function nextSkin(old: Id | undefined, skin: string | null | undefined, ctx: CommandContext): Id | undefined {
  if (skin === undefined) return old;
  return skin === null ? undefined : ctx.resolve('skin', skin);
}

/** The fix animations after `input`: null removes a base, a reference (re)maps it. */
function nextAnimations(old: Readonly<Record<string, Id>> | undefined, input: FixInput['animations'], ctx: CommandContext): Record<string, Id> {
  const animations: Record<string, Id> = { ...old };
  for (const [base, anim] of Object.entries(input ?? {})) {
    if (anim === null) Reflect.deleteProperty(animations, base);
    else animations[base] = ctx.resolve('animation', anim);
  }
  return animations;
}

/** The whole fix after `input`, as a new object; emptied parts are left out ({} = no fix). */
function nextFix(old: Fix | undefined, input: FixInput, ctx: CommandContext): Fix {
  const skin = nextSkin(old?.skin, input.skin, ctx);
  const ops = input.drawOrderOps === undefined ? [...(old?.drawOrderOps ?? [])] : input.drawOrderOps.map((op) => resolveOp(ctx, op));
  const animations = nextAnimations(old?.animations, input.animations, ctx);
  return { ...(skin === undefined ? {} : { skin }), ...(ops.length === 0 ? {} : { drawOrderOps: ops }), ...(Object.keys(animations).length === 0 ? {} : { animations }) };
}

/** Compares fixes part by part (no fix and an empty fix are equal). */
function sameFix(a: Fix | undefined, b: Fix): boolean {
  return JSON.stringify([a?.skin, a?.drawOrderOps ?? [], a?.animations ?? {}]) === JSON.stringify([b.skin, b.drawOrderOps ?? [], b.animations ?? {}]);
}

/** Stores `fix` on the entry, or removes the fix once it is empty (mirrorAsymmetry then warns again). */
function storeFix(entry: Draft<DirectionEntry>, fix: Fix): void {
  if (Object.keys(fix).length === 0) delete entry.fix;
  else entry.fix = fix;
}

/** direction.setFix: rewrites a mirrorFix entry's fix unless nothing changes; returns its open MIRROR_ASYMMETRY warnings. */
export const command = defineCommand({
  name: 'direction.setFix',
  description:
    'Edit the fix of a mirrorFix direction: skin (overlay skin, null removes), drawOrderOps (swap a/b, moveAbove|moveBelow slot/ref by slot name; replaces the list, [] clears), animations { base: animation | null } (played on the reserved upper track); returns the MIRROR_ASYMMETRY warnings still open for it.',
  input: z
    .object({ dir: prim.dir, skin: ref.nullable().exactOptional(), drawOrderOps: z.array(opSchema).exactOptional(), animations: z.record(prim.name, ref.nullable()).exactOptional() })
    .strict(),
  apply(draft, input, ctx) {
    const entry = directionsDraft(draft, 'dir').entries[input.dir];
    if (entry?.mode !== 'mirrorFix') throwError('VALIDATION', undefined, { path: 'dir', hint: `${input.dir} is not a mirrorFix direction; run direction.setMode { mode: "mirrorFix" } first` });
    const old = entry.fix === undefined ? undefined : currentOf(entry.fix);
    const fix = nextFix(old, input, ctx);
    if (!sameFix(old, fix)) {
      storeFix(entry, fix);
      draft.meta.updatedAt = ctx.now;
    }
    for (const w of mirrorAsymmetry(draft)) if (w.params?.dir === input.dir) ctx.warnings.push(w);
  },
});
